import { TComboboxBaseOption } from "src/ui/Combobox/Combobox"

/**
 * Generates an array of time options for use in a string based time picker dropdown
 *
 * @param interval - The interval in minutes between each time option (default: 15)
 * @param start - The starting time in minutes from midnight (default: 0)
 * @param end - The ending time in minutes from midnight (default: 1440 (24 * 60, 24 hours in minutes))
 * @param clockType - Whether to use "12" hour format with AM/PM or "24" hour format (default: "24")
 * @returns Array of time options with label and value properties
 *
 * @example
 * // 24-hour format, 15 min intervals starting at midnight
 * getTimePickerOptions({})
 * // Returns: [{label: "00:00", value: "00:00"}, {label: "00:15", value: "00:15"}, ...]
 *
 * @example
 * // 12-hour format, 30 min intervals starting at 9am
 * getTimePickerOptions({ interval: 30, start: 540, clockType: "12" })
 * // Returns: [{label: "9:00 AM", value: "09:00"}, {label: "9:30 AM", value: "09:30"}, ...]
 */
export function getTimePickerOptions({
  interval = 15,
  start = 0,
  clockType = "24",
  end = 24 * 60,
  isTimeBlocked,
}: {
  interval?: number
  start?: number
  clockType?: "12" | "24"
  end?: number
  isTimeBlocked?: (hour: number, minute: number) => boolean
}): TComboboxBaseOption[] {
  const isTwelveHour = clockType === "12"
  const options: TComboboxBaseOption[] = []

  let currentMinutes = start

  for (let i = 0; currentMinutes < end; i++) {
    const currentHour = Math.floor(currentMinutes / 60) // Transform minutes to hours, e.g. 121 -> 2
    const minutesOfTheHour = currentMinutes % 60 // Transform minutes to minutes of the hour, e.g. 121 -> 1

    options[i] = {
      label: getOptionTimeLabel(currentHour, minutesOfTheHour, isTwelveHour),
      value: getOptionTimeValue(currentHour, minutesOfTheHour),
      disabled: isTimeBlocked?.(currentHour, minutesOfTheHour) ?? false,
    }

    currentMinutes = currentMinutes + interval
  }

  return options
}

/**
 * Converts a time string formatted in 24-clock to a new string formatted in whatever clock type is specified
 *
 * @param time - The time string to convert
 * @param clockType - The clock type to use
 * @returns The converted time string
 * @example
 * convertInputValueToOptionLabel("13:45", "24")
 * // Returns: "13:45"
 * convertInputValueToOptionLabel("13:45", "12")
 * // Returns: "1:45 PM"
 */
export function convertInputValueToOptionLabel(
  time: TMTimePickerInputValue | null,
  clockType: "12" | "24"
) {
  const isTwelveHour = clockType === "12"
  if (!time) {
    return ""
  }

  const [hours, minutes] = time.split(":")

  if (!hours || !minutes) {
    return ""
  }

  const hour = parseInt(hours)
  const minute = parseInt(minutes)

  return getOptionTimeLabel(hour, minute, isTwelveHour)
}

/**
 * Formats a time as a display label, handling both 12-hour and 24-hour formats
 *
 * @param hour - The hour component (0-23)
 * @param minute - The minute component (0-59)
 * @param isTwelveHour - Whether to format in 12-hour format with AM/PM
 * @returns Formatted time string (e.g., "1:30 PM" or "13:30")
 */
function getOptionTimeLabel(
  hour: number,
  minute: number,
  isTwelveHour: boolean
): string {
  if (isTwelveHour) {
    // 12 hour format represents hour 0 as 12 AM, and hour 12 as 12 PM
    // Note that no padding is necessary for the hour, as it will always be 1 or 2 digits
    const formattedHour = (hour % 12 || 12).toString()
    const formattedMinute = minute.toString().padStart(2, "0")
    const suffix = ["AM", "PM"][Math.floor(hour / 12)]
    return `${formattedHour}:${formattedMinute} ${suffix}`
  }

  const formattedHour = hour.toString().padStart(2, "0")
  const formattedMinute = minute.toString().padStart(2, "0")
  return `${formattedHour}:${formattedMinute}`
}

/**
 * Formats a time as a value for internal use, always in 24-hour format
 *
 * @param hour - The hour component (0-23)
 * @param minute - The minute component (0-59)
 * @returns Formatted time string in 24-hour format (e.g., "13:30")
 */
export function getOptionTimeValue(
  hour: number,
  minute: number
): TMTimePickerInputValue {
  const formattedHour = hour.toString().padStart(2, "0")
  const formattedMinute = minute.toString().padStart(2, "0")
  return `${formattedHour}:${formattedMinute}` as TMTimePickerInputValue
}

/**
 * Finds the closest match in an array of time options based on the search input.
 * Uses levenshtein distance to find the closest match.
 *
 * TODO: Improve algorithm to be a bit smarter, e.g. searching for
 * "3 pm" should match "3:00 PM" and not "1:30 PM" which it does currently.
 *
 * @param timeOptions - Array of time options to search through
 * @param searchInput - The input string to search for
 * @returns The closest match or null if no match is found
 */
export function findClosestMatch(
  timeOptions: TComboboxBaseOption[],
  searchInput: string
): TComboboxBaseOption | null {
  let match: TComboboxBaseOption | null = null
  let closestDistance = Infinity

  for (const opt of timeOptions) {
    const distance = levenshteinDistance(
      normalizeMatchValue(searchInput),
      normalizeMatchValue(opt.label)
    )

    if (distance < closestDistance) {
      closestDistance = distance
      match = opt
    }
  }

  return match
}

function normalizeMatchValue(value: string): string {
  return value
    .trim()
    .toLocaleLowerCase()
    .replace(/^0+/, "") // remove leading 0s
    .replace(/[^a-z0-9]/g, "") // remove any non-alphanumeric characters, e.g. "12:30pm!" -> "1230pm"
}

/**
 * Compute the Levenshtein distance between two strings.
 * The Levenshtein distance is the minimum number of single-character edits
 * (insertions, deletions, or substitutions) required to change one string into the other.
 */
function levenshteinDistance(s1: string, s2: string): number {
  const s1Length = s1.length
  const s2Length = s2.length

  // Create a 2D array
  const distances: number[][] = Array.from({ length: s1Length + 1 }, () =>
    new Array<number>(s2Length + 1).fill(0)
  )

  // Initialize base cases
  for (let i = 0; i <= s1Length; i++) {
    distances[i]![0] = i
  }
  for (let j = 0; j <= s2Length; j++) {
    distances[0]![j] = j
  }

  // Fill the matrix
  for (let i = 1; i <= s1Length; i++) {
    for (let j = 1; j <= s2Length; j++) {
      if (s1[i - 1] === s2[j - 1]) {
        distances[i]![j] = distances[i - 1]![j - 1]! // no operation needed
      } else {
        distances[i]![j] = Math.min(
          distances[i - 1]![j]! + 1, // deletion
          distances[i]![j - 1]! + 1, // insertion
          distances[i - 1]![j - 1]! + 1 // substitution
        )
      }
    }
  }

  return distances[s1Length]![s2Length]!
}

/**
 * Type for a valid time select value.
 * Enforces:
 * - Hours from 00-23
 * - Minutes from 00-59
 * - Proper zero-padding
 * - Correct format with colon
 *
 * @example
 * // Valid
 * const time1: TValidTimePickerInputValue = "13:45"
 * const time2: TValidTimePickerInputValue = "00:00"
 * const time3: TValidTimePickerInputValue = "23:59"
 *
 * // Invalid (TypeScript errors)
 * const invalid1: TValidTimePickerInputValue = "24:00" // Hour too high
 * const invalid2: TValidTimePickerInputValue = "12:60" // Minute too high
 * const invalid3: TValidTimePickerInputValue = "9:30" // Missing leading zero
 */
type TDigit = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
type THour = `0${TDigit}` | `1${TDigit}` | `2${"0" | "1" | "2" | "3"}`
type TMinute = `${"0" | "1" | "2" | "3" | "4" | "5"}${TDigit}`
export type TMTimePickerInputValue = `${THour}:${TMinute}`

export function isTMTimePickerInputValue(
  value: string
): value is TMTimePickerInputValue {
  const [hours, minutes] = value.split(":")

  if (hours?.length !== 2 || minutes?.length !== 2) {
    return false
  }

  const hourNumber = Number(hours)
  const minuteNumber = Number(minutes)

  return (
    hourNumber >= 0 &&
    hourNumber <= 23 &&
    minuteNumber >= 0 &&
    minuteNumber <= 59
  )
}
